import React, { useState, useEffect } from 'react';
import axios from 'axios';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * Hook to fetch and manage resource state and handle real-time updates.
 *
 * @param {string} resourceId The ID of the resource to fetch.
 * @param {string} scope      The scope.
 * @param {string} scopeId    The scope ID.
 *
 * @param {object}  options            The hook options.
 * @param {object}  options.language   The requested language for the resource.
 * @param {boolean} options.skip_load  Flag indicating whether the resource load should be skipped.
 * @param {object}  options.socket     The TechChange socket instance to handle real-time events.
 *
 * @returns {array}  result
 * @returns {object} result[0]  The resource state.
 * @returns {func}   result[1]  Callback function to update the resource state.
 * @returns {func}   result[2]  The error state related to fetching the resource, if any.
 * @returns {func}   result[3]  Callback function to update the error state.
 */
const useResource = (resourceId, scope, scopeId, options = {}) => {

	const [resource, setResource] = useState(null);
	const [error, setError] = useState(false);

	// Fetch the root resource and build the nested tree of child resources
	useEffect(() => {

		if (!options.skip_load) {
			// Reset state if the ID changes...
			setResource(null);
			setError(false);
		}

		if (resourceId && scope && scopeId && !options.skip_load) {

			axiosClient.get(`/api/v2/resources/${resourceId}`, {
				params: {
					scope,
					scope_id: scopeId,
					language: options.language,
				}
			}).then((response) => {

				if (response?.data) {
					setResource(buildResourceTree(response.data, ''));
				}

			}).catch((err) => {

				console.error(err);
				setError(err);

			});

		}

	}, [resourceId, scope, scopeId, options.skip_load, options.language]);

	// Listen for real-time updates to the root resource and nested tree of child resources
	useEffect(() => {

		if (!resource || !options.socket) {
			return () => {};
		}

		const onRealTimeChange = (data) => {

			// Check to make sure that the real-time update applies to the root resource...
			if (data.scope === scope &&
				data.scope_id === scopeId &&
				data.path?.indexOf(resource.id) === 0) {
				setResource((prev) => updateResourceTree(prev, data, '', options.language));
			}

		};

		// Listen for real-time creation of the resource(s)
		options.socket.on('create:resource', onRealTimeChange);

		// Listen for real-time updates to the resource(s)
		options.socket.on('update:resource', onRealTimeChange);

		// Clean up the listener for real-time creation/updates to the resource(s)
		return () => {
			options.socket.removeListener('create:resource', onRealTimeChange);
			options.socket.removeListener('update:resource', onRealTimeChange);
		};

	}, [resource, scope, scopeId, options.socket, options.language]);

	return [
		resource,
		(newValue) => setResource(buildResourceTree(newValue, '')),
		error,
		setError,
	];

};

export default useResource;

/**
 * Recursively build the resource tree with extra information for a root resource.
 *
 * @param {object} node       The root resource.
 * @param {string} parentURL  The URL of the parent resource.
 *
 * @returns {object} result   The updated root resource.
 */
function buildResourceTree(node, parentURL) {

	// Create a copy of the resource
	const result = Object.assign({}, node, {
		url: `${parentURL}/${node.slug}`, // Include URL information
		children: node.children ? [...node.children] : [],
	});

	// Sort the child resources
	result.children.sort((child1, child2) => child1.weight - child2.weight);

	// Loop through and build out each child resource
	for (let i = 0, len = result.children.length; i < len; i++) {
		result.children[i] = buildResourceTree(result.children[i], result.url);
	}

	return result;

}

/**
 * Recursively build the resource tree with extra information for a root resource
 * and add/delete/update a specific resource in the tree.
 *
 * @param {object} node         The root resource.
 * @param {object} updatedNode  The resource that was added/deleted/updated.
 * @param {string} parentURL    The URL of the parent resource.
 * @param {string} language     The requested language for the resource.
 *
 * @returns {object} result     The updated root resource.
 */
function updateResourceTree(node, updatedNode, parentURL, language) {

	// Create a copy of the resource
	const result = Object.assign({}, node, {
		url: `${parentURL}/${node.slug}`, // Include URL information
		children: node.children ? [...node.children] : [],
	});

	// Check if we've found where a resource needs to be added/deleted/updated...

	const parentPath = updatedNode.path.split('#');
	const parentID = parentPath[parentPath.length - 2];

	if (result.id === parentID) {

		// Check if the resource was deleted
		if (updatedNode.deleted_at) {

			result.children = result.children.filter((temp) => temp.id !== updatedNode.id);

		} else {

			const index = result.children.findIndex((temp) => temp.id === updatedNode.id);

			// Check if the resource was added
			if (index === -1) {

				const nodeToAdd = buildResourceTree(updatedNode, result.url);

				result.children.push(nodeToAdd);

			// Check if the resource was updated
			} else {

				const oldNode = result.children[index];

				const updatedResourceContents = [...oldNode.resource_contents];

				for (const updatedContent of updatedNode.resource_contents) {

					const oldContentIdx = oldNode.resource_contents
						.findIndex((rContent) => rContent.language === updatedContent.language);

					// Check if the translation has already been loaded in the state and update in place
					if (oldContentIdx >= 0) {

						updatedResourceContents[oldContentIdx] = {
							...updatedContent,
						};

					// Otherwise add the updated translation to the state
					} else {

						updatedResourceContents.push({ ...updatedContent });

					}

				}

				const nodeToUpdate = buildResourceTree(
					{ ...oldNode, ...updatedNode, resource_contents: updatedResourceContents },
					result.url,
				);

				result.children[index] = nodeToUpdate;

			}

		}

		// Re-sort the child resources affected by the change
		result.children.sort((child1, child2) => child1.weight - child2.weight);

		return result;

	}

	// Otherwise, continue recursing through the resource tree...

	for (let i = 0, len = result.children.length; i < len; i++) {
		result.children[i] = updateResourceTree(result.children[i], updatedNode, result.url, language);
	}

	return result;

}
